import type { AfterResponseHook } from "ky";
import ky from "ky";

import { hideLoader, showLoader } from "@/redux/actions/loader";
import store from "@/redux/store";

import type { ApiContext } from "../../types";
import { authRequestsTyped } from "../../(requests)/authTyped";

import { handleSessionExpired } from "./sessionExpired";

const dispatch = store.dispatch;

// Regular authenticated session — retries a 403 once by refreshing the token, using
// whichever mechanism (cookie or header) the failed request itself used
export const refreshAuthTokenHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const isCookieMode = request.credentials === "include";
  const context = options.context as ApiContext | undefined;

  // Mirror old refreshLocalStorageTokens: keep spinner on during the refresh+retry window.
  // loaderEndHook already fired (hideLoader) for the 403; re-show here so the spinner
  // stays visible while we refresh the token and replay the original request.
  dispatch(showLoader());

  try {
    const newToken = await refreshAuthTokenOnce(isCookieMode);

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone() // clone so ky can still populate error.data for beforeError hooks
      .json()
      .catch((): null => null);

    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success.
    // Cookie mode: no Authorization, credentials:"include" carried over so the freshly
    // re-planted id_token cookie (side effect of the refresh above) is what's exercised —
    // no header fallback. Header mode: unchanged from before this hook was made mode-aware.
    const retriedRequest = isCookieMode
      ? new Request(request, { credentials: "include" })
      : new Request(request, { headers: withAuthorizationHeader(request.headers, newToken) });

    return ky.retry({ code: "TOKEN_REFRESHED", request: retriedRequest });
  } catch (err) {
    // No unconditional logOut() — mirrors old refreshLocalStorageTokens behavior.
    // handleSessionExpired redirects only on confirmed 401 (exhausted retries on
    // new_id_token), which is the signal for a genuinely expired refresh token.
    // Same handling regardless of mode — a failed refresh is a failed refresh either way.
    dispatch(hideLoader());
    handleSessionExpired(err);

    return undefined;
  }
};

function withAuthorizationHeader(headers: Headers, token: string): Headers {
  const newHeaders = new Headers(headers);

  newHeaders.set("Authorization", token);

  return newHeaders;
}

// Two independent dedup locks, one per mode — a cookie-mode 403 and a header-mode 403
// arriving close together (plausible: a page can have both pilot and non-pilot
// endpoints in flight at once) must never share an in-flight refresh. Sharing one would
// mean the second caller's actual network request is shaped by whichever mode got there
// first, not its own — silently reintroducing the cross-mode coupling this hook exists to
// avoid. Cost: in that rare race, two new_id_token calls fire instead of one.
let cookieModeRefreshPromise: Promise<string> | null = null;
let headerModeRefreshPromise: Promise<string> | null = null;

// Mirrors the workflow refresh pattern — uses authRequestsTyped.newIdToken (httpPublic)
// so there is no dependency on the authenticated ky instances or redux/actions/auth.
function refreshAuthTokenOnce(isCookieMode: boolean): Promise<string> {
  if (isCookieMode) {
    cookieModeRefreshPromise ??= runRefresh({}).finally(() => {
      cookieModeRefreshPromise = null;
    });

    return cookieModeRefreshPromise;
  }

  headerModeRefreshPromise ??= runRefresh({
    body: {
      id_token: localStorage.getItem("token") ?? undefined,
      refresh_token: localStorage.getItem("refreshToken") ?? undefined,
    },
  }).finally(() => {
    headerModeRefreshPromise = null;
  });

  return headerModeRefreshPromise;
}

function runRefresh(args: Parameters<typeof authRequestsTyped.newIdToken>[0]): Promise<string> {
  return authRequestsTyped.newIdToken(args).then((res) => {
    const { AccessToken, IdToken } = res.AuthenticationResult;

    store.dispatch({ payload: { AccessToken, IdToken }, type: "REFRESH_ACCESS_TOKEN" });

    return IdToken;
  });
}
